import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { useResource } from "@/lib/api";
import { PageHeader, Loading, ErrorNote } from "@/components/kit";
import { DEPTS, STATUS, field, primaryBtn } from "@/lib/ops";
import { TaskCard } from "@/components/ops/TaskCard";
import { TaskDrawer } from "@/components/ops/TaskDrawer";
import { NewTaskDialog } from "@/components/ops/NewTaskDialog";

export const useOpsMeta = () => useResource("/ops/meta").data;

export const Board = ({ dept }) => {
  const meta = useOpsMeta();
  const [q, setQ] = useState("");
  const [priority, setPriority] = useState("");
  const [owner, setOwner] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const qs = new URLSearchParams({ department: dept, ...(q && { q }), ...(priority && { priority }), ...(owner && { owner }) }).toString();
  const { data, error, loading, reload } = useResource(`/ops/tasks?${qs}`);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const cols = useMemo(() => STATUS.filter((s) => showClosed || s.key !== "cancelled"), [showClosed]);
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="board-filters">
        <div className="relative min-w-[200px] flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--cc-muted)]" />
          <input data-testid="board-search" className={`${field} pl-9`} placeholder="Search ID, title, next action…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select data-testid="board-filter-priority" className={`${field} w-auto`} value={priority} onChange={(e) => setPriority(e.target.value)}><option value="">All priorities</option>{["P0", "P1", "P2", "P3"].map((p) => <option key={p}>{p}</option>)}</select>
        <select data-testid="board-filter-owner" className={`${field} w-auto`} value={owner} onChange={(e) => setOwner(e.target.value)}><option value="">All owners</option>{meta?.owners.map((o) => <option key={o}>{o}</option>)}</select>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" data-testid="board-show-cancelled" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />Cancelled</label>
        <button data-testid="board-new-task" onClick={() => setCreating(true)} className={`${primaryBtn} flex items-center gap-1.5`}><Plus size={14} />{dept === "support" ? "New ticket" : "New task"}</button>
      </div>
      {loading ? <Loading /> : error ? <ErrorNote error={error} /> : (
        <div className="-mx-5 overflow-x-auto px-5 pb-4 md:-mx-10 md:px-10" data-testid={`board-${dept}`}>
          <div className="flex gap-3" style={{ minWidth: cols.length * 216 }}>
            {cols.map((c) => {
              const items = data.filter((t) => t.status === c.key);
              return (
                <section key={c.key} className="w-[204px] shrink-0 rounded-sm bg-[#E9E5DC]/70 p-2" data-testid={`column-${c.key}`}>
                  <header className="mb-2 flex items-center justify-between px-1"><h3 className="text-[11px] font-bold uppercase tracking-wider text-[var(--cc-navy)]">{c.label}</h3><span className="font-mono text-[11px] text-[var(--cc-muted)]" data-testid={`column-count-${c.key}`}>{items.length}</span></header>
                  <div className="space-y-2">{items.map((t) => <TaskCard key={t.id} t={t} onOpen={setOpen} />)}</div>
                </section>
              );
            })}
          </div>
        </div>
      )}
      <TaskDrawer id={open} onClose={() => setOpen(null)} onChanged={reload} meta={meta} onOpen={setOpen} />
      {meta && <NewTaskDialog open={creating} onClose={() => setCreating(false)} dept={dept} meta={meta} onCreated={reload} />}
    </div>
  );
};

export default function DeptBoard() {
  const { dept } = useParams();
  if (!DEPTS[dept]) return <ErrorNote error="Unknown department" />;
  return (
    <div>
      <PageHeader eyebrow={`Operations Centre · ${DEPTS[dept].blurb}`} title={`${DEPTS[dept].label} Board`} />
      <Board dept={dept} key={dept} />
    </div>
  );
}
